import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/api/users';

export interface UserDisplay {
  id: string;
  email: string;
  displayName?: string;
}

/**
 * Fetches all users once and returns a lookup function that resolves
 * userId → displayName/email. Cached for 5 minutes.
 *
 * Use when a page renders a raw `assignedToId` / userId UUID that operators
 * want to see as a human-readable name. Keep the raw UUID in `title=` so
 * power users can still hover-inspect.
 */
export function useUserLookup() {
  const q = useQuery({
    queryKey: ['users', 'lookup'],
    queryFn: () => usersApi.list(),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  const userMap = useMemo(() => {
    const map = new Map<string, UserDisplay>();
    for (const u of q.data ?? []) {
      map.set(u.id, {
        id: u.id,
        email: u.email,
        displayName: u.displayName || u.email,
      });
    }
    return map;
  }, [q.data]);

  function resolve(userId: string | null | undefined): string {
    if (!userId) return '미배정';
    const found = userMap.get(userId);
    return found?.displayName || found?.email || `${userId.slice(0, 8)}…`;
  }

  return { resolve, userMap, isLoading: q.isLoading, isError: q.isError };
}
